// State that survives the tree it was declared in.
//
// `clientSwitch.tsx` swaps the whole client, and the shell's launch state —
// which mode, which level, which seed — was `useState` in `App`, so a switch
// away and back landed on the menu with the board still in the store and
// every door from the menu opening a *new* game. The game survived; the shell
// forgot what it was playing.
//
// `useKept` is `useState` that writes through to a shelf keyed on an owner
// (the `Game`): the first mount reads `initial`, every later mount reads what
// the last one left. The shelf is a `WeakMap`, so it goes when the game does.
// No React and no DOM beyond the hooks.

import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

const SHELVES = new WeakMap<object, Map<string, unknown>>();

function shelf(owner: object): Map<string, unknown> {
  let kept = SHELVES.get(owner);
  if (!kept) {
    kept = new Map();
    SHELVES.set(owner, kept);
  }
  return kept;
}

/** `useState`, remembered across remounts under `key` for `owner`. */
export function useKept<T>(owner: object, key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const kept = shelf(owner);
  const [value, setValue] = useState<T>(() =>
    kept.has(key) ? (kept.get(key) as T) : typeof initial === "function" ? (initial as () => T)() : initial
  );
  // Written after commit rather than inside the updater, which must stay pure.
  useEffect(() => {
    kept.set(key, value);
  }, [kept, key, value]);
  return [value, setValue];
}

/** `useRef`, remembered the same way: the same box every mount. */
export function useKeptRef<T>(owner: object, key: string, initial: T): { current: T } {
  const kept = shelf(owner);
  if (!kept.has(key)) kept.set(key, { current: initial });
  return kept.get(key) as { current: T };
}

/**
 * The shelf as plain entries, and back — for the dev refresh
 * (`engine/game/devResume.ts`), which writes it out on `pagehide` and seeds it
 * before the first render. A value JSON cannot carry is left out; a ref
 * round-trips as a plain `{ current }`, which is all `useKeptRef` hands out.
 */
export function shelfEntries(owner: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of shelf(owner)) {
    try {
      if (value !== undefined) out[key] = JSON.parse(JSON.stringify(value));
    } catch {
      // Not JSON: forgotten on refresh, as everything was before.
    }
  }
  return out;
}

export function seedShelf(owner: object, entries: Record<string, unknown>): void {
  const kept = shelf(owner);
  for (const [key, value] of Object.entries(entries)) kept.set(key, value);
}
